import { ISharepointDataDto } from "../interfaces/ISharepointDataDto";
import authModule from "../../../utilities/AuthModule";
import { IFileToUpload } from "../interfaces/IFileToUpload";
import { OneDriveOverWriteEnum } from "../components/enums/oneDriveOverWriteEnum";
import { IOneDriveDataDto } from "../interfaces/IOneDriveDataDto";
import { BasePersistor } from "./BasePersistor";
import { IPersistor } from "../interfaces/IPersistor";
import { API } from "../config/constants";
import { OutlookService } from "./OutlookService";
import { SavingLocationState } from "../types";
import { ModeEnum } from "../components/enums/modeEnum";
import { IMailSendPayload } from "../interfaces/IMailSendPayload";
import { OutlookUtils } from "../../../utilities/OutlookUtils"
import { FolderTypeEnum } from "../components/enums/FolderTypeEnum";
import { logger } from "../../../utilities/logger";
import { CheckinTypes, ListSettings } from "../components/_v2/redux/api/local/api-local-interfaces";
import { FileMetaData } from "../interfaces/props/FileMetaData";
import { apiFileSystemCallDynamicEndpoint } from "./ApiService";
import { SaveMode } from "../components/enums/SaveModeEnum";

export class OutlookPersistor extends BasePersistor implements IPersistor {
  mailSendPayload: IMailSendPayload;
  mode: ModeEnum;
  disableMailCategorization?: boolean;
  constructor(mailSendPayload: IMailSendPayload, mode: ModeEnum, disableMailCategorization: boolean) {
    super();
    this.mailSendPayload = mailSendPayload;
    this.mode = mode;
    this.disableMailCategorization = disableMailCategorization;
  }

  saveToSharepoint = async (
    selectedFiles: IFileToUpload[],
    savingLocation: SavingLocationState,
    // folderPermissions: currentSharepointFolderState,
    autoFolderCreation: boolean,
    fileMetaData: FileMetaData[],
    accountId: string,
    informationProtectionId: string,
    outlookToken: string,
    outlookFolderType: FolderTypeEnum.sharepoint | FolderTypeEnum.oneDrive | FolderTypeEnum.teamChannel,
    autoRemoveMailItemAttachments?: boolean,
    convertEmailToPDF?: boolean,
    tenantOcrLanguage?: string,
    tenantPiiEnabled?: boolean,
    tenantReplaceFileNameWithGUID?: boolean,
    listSettings?: ListSettings,
    checkinType?: CheckinTypes,
    ensureUniqueFileName?: boolean,
    exchangeAccountEmail?: string,
    graphMailItemCategory?: string,
    saveMode?: SaveMode
  ): Promise<any> => {
    const userAuthData = await authModule.getAuthData();
    if (!userAuthData || !userAuthData.account) {
      authModule.signOut();
      return null;
    }

    let dataToSend: ISharepointDataDto = this.mode !== ModeEnum.mailSend ? {
      sharepointInfo: {
        graphToken: userAuthData.graphToken,
        sharepointToken: userAuthData.sharePointToken,
        // informationProtectionToken: userAuthData.informationProtectionToken,
        mailToken: userAuthData.mailboxToken,
        siteUrl: savingLocation.webUrl,
        documentLibrary: savingLocation.documentLibrary,
        sharepointListId: savingLocation.sharePointListId,
        directory: savingLocation.directory,
        // autoFolderCreation: !folderPermissions.canView
        autoFolderCreation: autoFolderCreation,
        ensureUniqueFileName,
        checkinType: checkinType
      },
      outlookRestInfo: {
        url: await OutlookUtils.getRestURL(),
        outlookToken: outlookToken,
        ewsUrl: OutlookUtils.getEWSURL(),
        ewsToken: await OutlookUtils.getEWSToken(),
        ewsMailItemId: OutlookService.getItemEWSId(Office.context.mailbox?.item?.itemId)
      },
      emailItemId: OutlookService.getItemRestId(Office.context.mailbox?.item?.itemId),
      fileContents: selectedFiles,
      fileMetadataList: fileMetaData.map(file => {
        return {
          fileId: file.fileId,
          metaData: file.metaData.map(input => ({ type: input?.type, internalName: input?.internalName, value: this.getInputValue(input?.value) }))
        }
      }),
      tenantGuid: userAuthData.account.tenantId,
      accountId: accountId,
      username: userAuthData.account.username,
      sensitivityLabelId: informationProtectionId,
      listSettings,
      exchangeAccountEmail,
      graphMailItemCategory
    } : {
      sharepointInfo: {
        graphToken: userAuthData.graphToken,
        sharepointToken: userAuthData.sharePointToken,
        // informationProtectionToken: userAuthData.informationProtectionToken,
        mailToken: userAuthData.mailboxToken,
        siteUrl: savingLocation.webUrl,
        documentLibrary: savingLocation.documentLibrary,
        sharepointListId: savingLocation.sharePointListId,
        directory: savingLocation.directory,
        autoFolderCreation: autoFolderCreation,
        ensureUniqueFileName: false,
        checkinType: checkinType
      },
      outlookRestInfo: {
        url: this.mailSendPayload.restURL,
        outlookToken: this.mailSendPayload.outlookToken,
        ewsUrl: this.mailSendPayload.ewsUrl,
        ewsToken: this.mailSendPayload.ewsToken,
        ewsMailItemId: this.mailSendPayload.emailItem?.ewsId
      },
      emailItemId: this.mailSendPayload.emailItem.id, //already in REST format
      fileContents: selectedFiles,
      fileMetadataList: fileMetaData.map(file => {
        return {
          fileId: file.fileId,
          metaData: file.metaData.map(input => ({ type: input?.type, internalName: input?.internalName, value: this.getInputValue(input?.value) }))
        }
      }),
      tenantGuid: userAuthData.account.tenantId,
      accountId: accountId,
      username: userAuthData.account.username,
      sensitivityLabelId: informationProtectionId,
      listSettings
    };

    dataToSend.categoriesToAssign = !this.disableMailCategorization ? await OutlookUtils.getCategoriesToAssign(dataToSend.emailItemId, outlookFolderType) : null;
    dataToSend.autoRemoveMailItemAttachments = autoRemoveMailItemAttachments === true;
    dataToSend.convertEmailToPDF = convertEmailToPDF === true;
    dataToSend.tenantOcrLanguage = tenantOcrLanguage;
    dataToSend.tenantApiKey = authModule.getTenantApiKey();
    dataToSend.tenantPiiEnabled = tenantPiiEnabled;
    dataToSend.tenantReplaceFileNameWithGUID = tenantReplaceFileNameWithGUID;
    dataToSend.exchangeAccountEmail = exchangeAccountEmail;
    dataToSend.graphMailItemCategory = graphMailItemCategory;
    dataToSend.saveMode = saveMode;
    dataToSend.teamId = savingLocation.teamId;
    dataToSend.channelId = savingLocation.channelId;

    try {
      const result = await apiFileSystemCallDynamicEndpoint(API.url.outlook.sharepoint, false, dataToSend);

      if (!this.disableMailCategorization && result?.categoryUpdateRequired === true) {
        OutlookUtils.setEWSItemCategory(dataToSend.outlookRestInfo.ewsMailItemId, dataToSend.categoriesToAssign);
      }

      return result;
    } catch (e) {
      logger(e, "OutlookPersistor.ts (saveToSharePoint)");
      if (e.response) {
        return e.response.data
      }
      return null;
    }
  };

  saveToOneDrive = async (
    files: IFileToUpload[],
    directory: string,
    oneDriveOverwriteOption: OneDriveOverWriteEnum,
    outlookToken: string,
    folderName: string = "",
    accountId: string,
    autoRemoveMailItemAttachments?: boolean,
    convertEmailToPDF?: boolean,
    tenantOcrLanguage?: string,
    exchangeAccountEmail?: string,
    graphMailItemCategory?: string,
    saveMode?: SaveMode
  ) => {
    let dataToSend: IOneDriveDataDto = this.mode !== ModeEnum.mailSend ? {
      graphToken: await authModule.getGraphAccessToken(),
      mailToken: authModule.getPersistedAuthState()?.mailboxToken && await authModule.getMailboxAccessToken(),
      emailItemId: OutlookService.getItemRestId(Office.context.mailbox?.item?.itemId),
      accountId: accountId,
      fileContents: files.filter(file => file.isSelected),
      outlookRestInfo: {
        url: await OutlookUtils.getRestURL(),
        outlookToken: outlookToken,
        ewsUrl: OutlookUtils.getEWSURL(),
        ewsToken: await OutlookUtils.getEWSToken(),
        ewsMailItemId: OutlookService.getItemEWSId(Office.context.mailbox?.item?.itemId)
      },
      directory: this.getSavingDirectory(directory, folderName),
      overWriteType: oneDriveOverwriteOption
    } : {
      graphToken: await authModule.getGraphAccessToken(),
      mailToken: authModule.getPersistedAuthState()?.mailboxToken && await authModule.getMailboxAccessToken(),
      emailItemId: this.mailSendPayload.emailItem.id,
      accountId: accountId,
      fileContents: files.filter(file => file.isSelected),
      outlookRestInfo: {
        url: this.mailSendPayload.restURL,
        outlookToken: this.mailSendPayload.outlookToken,
        ewsUrl: this.mailSendPayload.ewsUrl,
        ewsToken: this.mailSendPayload.ewsToken,
        ewsMailItemId: this.mailSendPayload.emailItem?.ewsId
      },
      directory: this.getSavingDirectory(directory, folderName),
      overWriteType: oneDriveOverwriteOption
    };

    dataToSend.categoriesToAssign = !this.disableMailCategorization ? await OutlookUtils.getCategoriesToAssign(dataToSend.emailItemId, FolderTypeEnum.oneDrive) : null;
    dataToSend.autoRemoveMailItemAttachments = autoRemoveMailItemAttachments === true;
    dataToSend.convertEmailToPDF = convertEmailToPDF === true;
    dataToSend.tenantOcrLanguage = tenantOcrLanguage;
    dataToSend.exchangeAccountEmail = exchangeAccountEmail;
    dataToSend.graphMailItemCategory = graphMailItemCategory;
    dataToSend.saveMode = saveMode;

    try {
      const result = await apiFileSystemCallDynamicEndpoint(API.url.outlook.oneDrive, true, dataToSend);

      if (!this.disableMailCategorization && result?.categoryUpdateRequired === true) {
        OutlookUtils.setEWSItemCategory(dataToSend.outlookRestInfo.ewsMailItemId, dataToSend.categoriesToAssign);
      }

      return result;
    } catch (e) {
      logger(e, "OutlookPersistor.ts (saveToOneDrive)");
      if (e.response) {
        return e.response.data
      }
      return null;
    }
  };
}
